import kotlinx.browser.localStorage
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlin.js.Date

@Serializable
data class JwtClaims(
    val email: String? = null,
    @SerialName("global_role") val globalRole: String? = null,
    /** Unix seconds; used client-side to auto-logout once the token expires (see Main.kt). */
    val exp: Long? = null,
)

/** `atob` is a browser global (base64 -> binary string); declared as external for Kotlin/JS interop. */
external fun atob(data: String): String

private val jwtJson = Json { ignoreUnknownKeys = true }

/**
 * Decodes the JWT payload client-side just to read email/global_role for display and UI
 * role-gating. Not a security boundary — RLS and the RPC role checks on the server are what
 * actually enforce permissions; this only decides what buttons/forms to show.
 */
fun decodeJwtClaims(token: String): JwtClaims? {
    return try {
        val payload = token.split(".").getOrNull(1) ?: return null
        var normalized = payload.replace('-', '+').replace('_', '/')
        normalized += "=".repeat((4 - normalized.length % 4) % 4)
        jwtJson.decodeFromString<JwtClaims>(atob(normalized))
    } catch (e: Throwable) {
        null
    }
}

private const val AUTH_TOKEN_KEY = "auth_token"

/**
 * Restores the session across page reloads/tab reopen: reads the last access token from
 * localStorage and returns it only if not yet expired (so a stale token doesn't silently
 * resurrect a "logged in" UI that the server would reject anyway).
 */
fun loadPersistedToken(): String? {
    val stored = localStorage.getItem(AUTH_TOKEN_KEY) ?: return null
    val exp = decodeJwtClaims(stored)?.exp
    if (exp != null && exp * 1000.0 <= Date().getTime()) {
        localStorage.removeItem(AUTH_TOKEN_KEY)
        return null
    }
    return stored
}

fun persistToken(token: String?) {
    if (token == null) localStorage.removeItem(AUTH_TOKEN_KEY) else localStorage.setItem(AUTH_TOKEN_KEY, token)
}

fun String?.canManage(): Boolean = this == "admin" || this == "superadmin"

fun String?.isSuperadmin(): Boolean = this == "superadmin"

fun String?.canScore(): Boolean = this == "scorer" || this == "admin" || this == "superadmin"
